import React, { useEffect, useRef, useState } from 'react';
import OpenSeadragon from 'openseadragon';
import {
    createDZITileSource,
    getImageItemId,
    getMacroscopicImage,
} from '../../utils/imageHelpers';
import {
    applyTiledImageTransform,
    findCoregMovingLayer,
    getCoregLayerFingerprint,
} from '../../utils/coregRegistration';
import { resetViewerBeforeOpen } from '../../utils/openseadragonHelpers';
import './OpenSeadragonViewer.css';

const OpenSeadragonViewer = ({
    selectedFolder,
    selectedImage,
    isNavigationViewer = false,
    isDermoscopyViewer = false,
    roiVisibilities = {},
    activeTiledOverlays = {},
    selectedRegistrationIndex = null,
    dermCoregImage = null,
    coregState = null,
    dermCoregOpacity = 0,
}) => {
    const viewerRef = useRef(null);
    const osdInstanceRef = useRef(null);
    const viewerContainerRef = useRef(null);
    /** Invalidates in-flight open callbacks when selection changes (avoid stale ROI draws). */
    const loadGenerationRef = useRef(0);
    const [viewer, setViewer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const loadingTimeoutRef = useRef(null);
    const [mouseCoords, setMouseCoords] = useState({
        webPoint: { x: 0, y: 0 },
        imagePoint: { x: 0, y: 0 },
        viewportPoint: { x: 0, y: 0 }
    });
    const [currentSlice, setCurrentSlice] = useState(0);
    const [maxSlices, setMaxSlices] = useState(0);
    const roiOverlaysRef = useRef({}); // Store references to ROI overlays by index
    const tiledOverlaysRef = useRef({}); // Store references to tiled image overlays by ROI index
    const dermCoregOverlayRef = useRef(null);
    const dermCoregOverlayDermIdRef = useRef(null);
    const dermCoregOverlayFingerprintRef = useRef(null);
    const dermCoregOverlayGenRef = useRef(0);
    const dermCoregOpacityRef = useRef(dermCoregOpacity);
    const selectedRegistrationIndexRef = useRef(selectedRegistrationIndex);
    const syncDermCoregOverlayRef = useRef(() => {});
    dermCoregOpacityRef.current = dermCoregOpacity;
    selectedRegistrationIndexRef.current = selectedRegistrationIndex;

    // Expose viewer to console for debugging
    // eslint-disable-next-line react-hooks/exhaustive-deps -- assign latest drawROIs helper without re-running on its identity
    useEffect(() => {
        if (viewer) {
            const viewerType = isNavigationViewer ? 'NAV' : isDermoscopyViewer ? 'DERM' : 'MAIN';
            window[`${viewerType}_VIEWER`] = viewer;
            window[`${viewerType}_ROI_OVERLAYS`] = roiOverlaysRef.current;

            // Expose drawROIsFromRegistrationData for macroscopic viewer only
            if (!isNavigationViewer && !isDermoscopyViewer) {
                window.MAIN_VIEWER_DRAW_ROIS = drawROIsFromRegistrationData;
            }

            console.log(`🔍 ${viewerType} viewer exposed to console as window.${viewerType}_VIEWER`);
            console.log(`🔍 ${viewerType} ROI overlays exposed as window.${viewerType}_ROI_OVERLAYS`);
            if (!isNavigationViewer && !isDermoscopyViewer) {
                console.log(`🔍 Draw ROIs function exposed as window.MAIN_VIEWER_DRAW_ROIS`);
            }
            console.log(`   Use DERM_VIEWER for dermoscopy, MAIN_VIEWER for macroscopic, NAV_VIEWER for RCM`);
        }
    }, [viewer, isNavigationViewer, isDermoscopyViewer]);

    // No placeholder needed - just show empty viewer if no image

    // Helper function to load z-stack - load first slice with viewer.open(), then background load rest
    const loadZStack = (viewer, image, maxSlices, initialSlice = 0, onLoaded = null, generation = null) => {
        const isStale = () => generation != null && generation !== loadGenerationRef.current;
        const startTime = performance.now();
        console.log('=== LOADING Z-STACK ===');
        console.log(`⏱️  Start time: ${startTime.toFixed(2)}ms`);
        console.log(`Loading initial slice ${initialSlice + 1}/${maxSlices} with viewer.open()`);

        // Don't clear here - already cleared by the calling effect

        // Use viewer.open() just like single images do for fast initial load
        const firstSliceUrl = createDZITileSource(image, initialSlice);
        console.log(`📍 Opening with viewer.open(): ${firstSliceUrl}`);

        const openStart = performance.now();
        console.log(`⏱️  About to call viewer.open() at: ${openStart.toFixed(2)}ms`);
        viewer.open(firstSliceUrl);
        console.log(`⏱️  viewer.open() call returned after: ${(performance.now() - openStart).toFixed(2)}ms`);

        // After the first slice opens, wait for it to render before loading others
        viewer.addOnceHandler('open', () => {
            if (isStale()) return;
            const openedTime = performance.now();
            console.log(`✅ First slice opened (metadata loaded) after ${(openedTime - startTime).toFixed(2)}ms`);

            // Wait for the first tile to actually be DRAWN before loading other slices
            const tiledImage = viewer.world.getItemAt(0);
            if (tiledImage) {
                tiledImage.addOnceHandler('fully-loaded-change', () => {
                    if (isStale()) return;
                    const fullyLoadedTime = performance.now();
                    console.log(`🎨 First slice tiles FULLY RENDERED after ${(fullyLoadedTime - startTime).toFixed(2)}ms`);

                    if (onLoaded) onLoaded();

                    // NOW load the rest of the slices in the background (after first is visible)
                    console.log(`⏳ Now background loading ${maxSlices - 1} additional slices...`);
                    for (let i = 0; i < maxSlices; i++) {
                        if (i === initialSlice) continue; // Skip the one we just loaded
                        if (isStale()) return;

                        const sliceUrl = createDZITileSource(image, i);
                        viewer.addTiledImage({
                            tileSource: sliceUrl,
                            opacity: 0.0,
                            index: i
                        });
                    }
                });
            }
        });

        console.log('=== Z-STACK LOADING INITIATED ===');
    };

    // Helper function to update slice visibility - just change opacity for flicker-free transitions
    const updateSliceVisibility = (viewer, currentSlice, maxSlices) => {
        if (!viewer || maxSlices <= 1) return;

        // Simply toggle opacity - no flickering!
        for (let i = 0; i < maxSlices; i++) {
            const tiledImage = viewer.world.getItemAt(i);
            if (tiledImage) {
                tiledImage.setOpacity(i === currentSlice ? 1.0 : 0.0);
            }
        }
    };

    // Debug state changes
    // useEffect(() => {
    //     console.log('Mouse coords state updated:', mouseCoords);
    // }, [mouseCoords]);

    // ROI drawing functions
    const clearROIs = () => {
        if (viewer) {
            viewer.clearOverlays();
            roiOverlaysRef.current = {}; // Clear our references too
        }
    };

    // Update visibility of a specific ROI without redrawing
    const updateROIVisibility = (index, isVisible) => {
        console.log(`👁️ updateROIVisibility called for ROI ${index}:`, isVisible);
        const overlayElement = roiOverlaysRef.current[index];
        console.log(`👁️ Found overlay element:`, overlayElement);
        if (overlayElement) {
            const oldOpacity = overlayElement.style.opacity;
            overlayElement.style.opacity = isVisible ? '1' : '0';
            console.log(`👁️ Updated ROI ${index} opacity: ${oldOpacity} -> ${overlayElement.style.opacity}`);
        } else {
            console.warn(`❌ No overlay element found for ROI ${index}`);
            console.warn(`Available overlays:`, Object.keys(roiOverlaysRef.current));
        }
    };

    const applyROIBoxStyle = (overlayElement, index) => {
        const isSelected = selectedRegistrationIndexRef.current === index;
        overlayElement.classList.toggle('roi-overlay--selected', isSelected);
        overlayElement.style.borderStyle = isSelected ? 'dashed' : 'solid';
        overlayElement.style.borderWidth = isSelected ? '3px' : '2px';
    };

    const updateROISelectionStyles = () => {
        Object.entries(roiOverlaysRef.current).forEach(([index, overlayElement]) => {
            applyROIBoxStyle(overlayElement, parseInt(index, 10));
        });
    };

    // Add a tiled image overlay at the specified coordinates
    const addTiledImageOverlay = async (registrationItem, roiIndex) => {
        if (!viewer || !registrationItem) return;

        console.log(`🖼️ Adding tiled image overlay for ROI ${roiIndex}:`, registrationItem);

        // If this ROI already has an overlay, remove it first
        if (tiledOverlaysRef.current[roiIndex]) {
            console.log(`🗑️ Removing existing overlay for ROI ${roiIndex}`);
            viewer.world.removeItem(tiledOverlaysRef.current[roiIndex]);
            delete tiledOverlaysRef.current[roiIndex];
        }

        // Get coordinates from registration item
        const { x_min, x_max, y_min, y_max, yamlId, _id } = registrationItem;

        if (!yamlId && !_id) {
            console.warn('No image ID found in registration item');
            return;
        }

        // Get the base tiled image (macroscopic) to compute correct positioning
        const baseTiledImage = viewer.world.getItemAt(0);
        if (!baseTiledImage) {
            console.warn('No base image found in viewer');
            return;
        }

        // Create tile source for the overlay image
        const tileSourceUrl = createDZITileSource(registrationItem, 0);

        // Use the SAME coordinate conversion as ROI boxes
        const x = parseFloat(x_min) || 0;
        const y = parseFloat(y_min) || 0;
        const width = (parseFloat(x_max) || 0) - (parseFloat(x_min) || 0);
        const height = (parseFloat(y_max) || 0) - (parseFloat(y_min) || 0);

        // Skip overlays with zero or negative dimensions (invalid coordinates)
        if (width <= 0 || height <= 0) {
            console.warn(`⚠️  Skipping tiled overlay ${roiIndex} with invalid dimensions:`, { width, height, x_min, x_max, y_min, y_max });
            return;
        }

        console.log(`🖼️ Image pixel coordinates for ROI ${roiIndex}:`, { x, y, width, height });

        // Convert image coordinates to viewport coordinates (same as ROI boxes)
        const viewportRect = baseTiledImage.imageToViewportRectangle(x, y, width, height);

        console.log(`🖼️ Viewport coordinates for ROI ${roiIndex}:`, {
            x: viewportRect.x,
            y: viewportRect.y,
            width: viewportRect.width,
            height: viewportRect.height
        });

        // Add the tiled image to the viewer at the specified bounds
        viewer.addTiledImage({
            tileSource: tileSourceUrl,
            opacity: 0.7, // Semi-transparent so we can see both images
            x: viewportRect.x,
            y: viewportRect.y,
            width: viewportRect.width,
            success: (event) => {
                console.log(`✅ Tiled image overlay added successfully for ROI ${roiIndex}:`, event.item);
                tiledOverlaysRef.current[roiIndex] = event.item;
            },
            error: (error) => {
                console.error(`❌ Error adding tiled image overlay for ROI ${roiIndex}:`, error);
            }
        });
    };

    // Remove a specific tiled image overlay by ROI index
    const removeTiledImageOverlay = (roiIndex) => {
        if (!viewer) return;

        console.log(`🗑️ Removing tiled overlay for ROI ${roiIndex}`);

        const tiledImage = tiledOverlaysRef.current[roiIndex];
        if (tiledImage) {
            viewer.world.removeItem(tiledImage);
            delete tiledOverlaysRef.current[roiIndex];
            console.log(`✅ Removed overlay for ROI ${roiIndex}`);
        } else {
            console.warn(`⚠️ No overlay found for ROI ${roiIndex}`);
        }
    };

    const clearDermCoregOverlayRefs = () => {
        dermCoregOverlayRef.current = null;
        dermCoregOverlayDermIdRef.current = null;
        dermCoregOverlayFingerprintRef.current = null;
    };

    const isDermCoregOverlayInWorld = () => {
        const tiledImage = dermCoregOverlayRef.current;
        if (!viewer || !tiledImage) return false;
        return viewer.world.getIndexOfItem(tiledImage) >= 0;
    };

    const removeDermCoregOverlay = () => {
        dermCoregOverlayGenRef.current += 1;
        if (!viewer || !dermCoregOverlayRef.current) {
            clearDermCoregOverlayRefs();
            return;
        }
        if (isDermCoregOverlayInWorld()) {
            viewer.world.removeItem(dermCoregOverlayRef.current);
        }
        clearDermCoregOverlayRefs();
    };

    const syncDermCoregOverlay = () => {
        if (!viewer || isNavigationViewer || isDermoscopyViewer) return;

        if (!dermCoregImage || !coregState) {
            removeDermCoregOverlay();
            return;
        }

        const baseImage = viewer.world.getItemAt(0);
        if (!baseImage) return;

        const dermId = getImageItemId(dermCoregImage);
        const layerState = findCoregMovingLayer(coregState, dermCoregImage);
        if (!dermId || !layerState) {
            removeDermCoregOverlay();
            return;
        }

        const opacity = dermCoregOpacityRef.current;
        const fingerprint = getCoregLayerFingerprint(layerState);

        if (isDermCoregOverlayInWorld()) {
            if (
                dermCoregOverlayDermIdRef.current === dermId
                && dermCoregOverlayFingerprintRef.current === fingerprint
            ) {
                dermCoregOverlayRef.current.setOpacity(opacity);
                return;
            }
            removeDermCoregOverlay();
        } else if (dermCoregOverlayRef.current) {
            clearDermCoregOverlayRefs();
        }

        if (opacity <= 0) return;

        const tileSourceUrl = createDZITileSource(dermCoregImage, 0);
        if (!tileSourceUrl) return;

        const generation = ++dermCoregOverlayGenRef.current;
        viewer.addTiledImage({
            tileSource: tileSourceUrl,
            opacity: 0,
            success: (event) => {
                if (generation !== dermCoregOverlayGenRef.current) {
                    viewer.world.removeItem(event.item);
                    return;
                }
                const tiledImage = event.item;
                applyTiledImageTransform(tiledImage, layerState);
                tiledImage.setOpacity(dermCoregOpacityRef.current);
                viewer.world.setItemIndex(tiledImage, viewer.world.getItemCount() - 1);
                dermCoregOverlayRef.current = tiledImage;
                dermCoregOverlayDermIdRef.current = dermId;
                dermCoregOverlayFingerprintRef.current = fingerprint;
            },
            error: (error) => {
                console.error('❌ Failed to add derm co-registration overlay:', error);
            },
        });
    };

    syncDermCoregOverlayRef.current = syncDermCoregOverlay;

    const drawROIFromCoordinates = (coordinates, index) => {
        if (!viewer) return;

        const { x_min, x_max, y_min, y_max } = coordinates;

        // Convert table coordinates to viewport coordinates
        const x = parseFloat(x_min) || 0;
        const y = parseFloat(y_min) || 0;
        const width = (parseFloat(x_max) || 0) - (parseFloat(x_min) || 0);
        const height = (parseFloat(y_max) || 0) - (parseFloat(y_min) || 0);

        // Skip ROIs with zero or negative dimensions (invalid coordinates)
        if (width <= 0 || height <= 0) {
            console.warn(`⚠️  Skipping ROI ${index} with invalid dimensions:`, { width, height, x_min, x_max, y_min, y_max });
            return;
        }

        // Get visibility for this ROI (default to true if not set)
        const isVisible = roiVisibilities[index] !== undefined ? roiVisibilities[index] : true;

        // Check if we have a tiled image to get proper coordinate conversion
        const tiledImage = viewer.world.getItemAt(0);
        if (tiledImage) {
            // Convert image coordinates to viewport coordinates
            const imageRect = tiledImage.imageToViewportRectangle(x, y, width, height);

            // Create overlay element
            const overlayElement = document.createElement("div");
            overlayElement.className = 'roi-overlay';
            overlayElement.setAttribute('data-roi-index', index);
            overlayElement.style.borderColor = getROIColor(index);
            applyROIBoxStyle(overlayElement, index);
            overlayElement.style.visibility = 'visible';
            overlayElement.style.position = 'absolute';
            overlayElement.style.pointerEvents = 'none';
            overlayElement.style.backgroundColor = 'rgba(255, 255, 255, 0)';
            overlayElement.style.opacity = isVisible ? '1' : '0';

            roiOverlaysRef.current[index] = overlayElement;

            viewer.addOverlay(overlayElement, imageRect);
        } else {
            const viewportRect = new OpenSeadragon.Rect(x, y, width, height);

            const overlayElement = document.createElement('div');
            overlayElement.className = 'roi-overlay';
            overlayElement.setAttribute('data-roi-index', index);
            overlayElement.style.borderColor = getROIColor(index);
            applyROIBoxStyle(overlayElement, index);
            overlayElement.style.visibility = 'visible';
            overlayElement.style.position = 'absolute';
            overlayElement.style.pointerEvents = 'none';
            overlayElement.style.backgroundColor = 'rgba(255, 255, 255, 0)';

            roiOverlaysRef.current[index] = overlayElement;

            viewer.addOverlay(overlayElement, viewportRect);
        }
    };

    // Generate visually distinct colors using HSL color space, return as hex
    const getROIColor = (index) => {
        // Distribute hue values evenly around the color wheel for maximum distinction
        const total = 20; // Expected max number of ROIs
        const saturation = 85;
        const lightness = 50;
        const hue = (index * 360 / total) % 360;

        // Convert HSL to RGB
        const s = saturation / 100;
        const l = lightness / 100;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((hue / 60) % 2 - 1));
        const m = l - c / 2;

        let r = 0, g = 0, b = 0;
        if (hue >= 0 && hue < 60) {
            r = c; g = x; b = 0;
        } else if (hue >= 60 && hue < 120) {
            r = x; g = c; b = 0;
        } else if (hue >= 120 && hue < 180) {
            r = 0; g = c; b = x;
        } else if (hue >= 180 && hue < 240) {
            r = 0; g = x; b = c;
        } else if (hue >= 240 && hue < 300) {
            r = x; g = 0; b = c;
        } else {
            r = c; g = 0; b = x;
        }

        // Convert to 0-255 range and then to hex
        const toHex = (value) => {
            const hex = Math.round((value + m) * 255).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };

        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    // Calculate image bounds from a, b, c, d coordinates
    const calculateImageBounds = (item) => {
        const parseCoord = (coord) => {
            if (Array.isArray(coord)) return coord;
            if (typeof coord === 'string') {
                const match = coord.match(/\[([^,]+),\s*([^\]]+)\]/);
                if (match) {
                    const x = parseFloat(match[1]);
                    const y = parseFloat(match[2]);
                    if (!isNaN(x) && !isNaN(y)) return [x, y];
                }
            }
            return null;
        };

        const a = parseCoord(item.a);
        const b = parseCoord(item.b);
        const c = parseCoord(item.c);
        const d = parseCoord(item.d);

        const allCoords = [a, b, c, d].filter(coord => coord !== null);
        if (allCoords.length === 0) {
            return { min_x: 0, max_x: 0, min_y: 0, max_y: 0 };
        }

        const allX = allCoords.map(coord => coord[0]);
        const allY = allCoords.map(coord => coord[1]);

        return {
            min_x: Math.min(...allX),
            max_x: Math.max(...allX),
            min_y: Math.min(...allY),
            max_y: Math.max(...allY)
        };
    };

    // Use pre-computed coordinates from admin app
    // The admin app should have already computed the proper transformed coordinates
    const addComputedCoordinates = (registrationData) => {
        if (!Array.isArray(registrationData)) return registrationData;

        console.log('📊 Using pre-computed coordinates from admin app');

        return registrationData.map(item => {
            // Check if coordinates are already computed
            if (item.x_min !== undefined && item.x_max !== undefined &&
                item.y_min !== undefined && item.y_max !== undefined) {
                console.log(`✅ Item ${item.description} has pre-computed coordinates:`, {
                    x_min: item.x_min,
                    x_max: item.x_max,
                    y_min: item.y_min,
                    y_max: item.y_max
                });
                return item;
            }

            // If no pre-computed coordinates, log warning and use fallback
            console.warn(`⚠️  Item ${item.description} missing pre-computed coordinates, using fallback`);
            const bounds = calculateImageBounds(item);
            return {
                ...item,
                x_min: bounds.min_x,
                x_max: bounds.max_x,
                y_min: bounds.min_y,
                y_max: bounds.max_y
            };
        });
    };

    const drawROIsFromRegistrationData = (registrationData) => {
        if (!viewer || !registrationData || isDermoscopyViewer) return;

        // Add computed coordinates if missing
        const processedData = addComputedCoordinates(registrationData);

        // Clear existing ROIs
        clearROIs();

        // Draw ROIs for each item in registration data
        processedData.forEach((item, index) => {
            const hasBounds =
                item.x_min != null &&
                item.x_min !== '' &&
                item.x_max != null &&
                item.x_max !== '' &&
                item.y_min != null &&
                item.y_min !== '' &&
                item.y_max != null &&
                item.y_max !== '';
            if (hasBounds) {
                drawROIFromCoordinates(item, index);
            }
        });
    };

    // Initialize OpenSeadragon viewer
    useEffect(() => {
        if (!viewerRef.current) return;

        const options = {
            element: viewerRef.current,
            prefixUrl: "/openseadragon-images/",
            tileSources: [],
            showNavigationControl: true,
            showSequenceControl: false,
            showFullPageControl: true,
            showZoomControl: true,
            showHomeControl: true,
            showRotationControl: true,
            minZoomLevel: 0.1,  // Allow zooming out to 10% of image size
            maxZoomLevel: 50,   // Allow zooming in to 50x
            animationTime: 0.1  // Very fast animation (default is 1.2 seconds)
        };

        const osdViewer = new OpenSeadragon.Viewer(options);
        osdInstanceRef.current = osdViewer;
        setViewer(osdViewer);

        // Add global error handler to suppress forceRedraw errors
        const originalError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
            if (message && message.includes('forceRedraw')) {
                console.warn('Suppressed OpenSeadragon forceRedraw error:', message);
                return true; // Prevent the error from propagating
            }
            if (originalError) {
                return originalError(message, source, lineno, colno, error);
            }
            return false;
        };

        // Add error handlers to prevent forceRedraw errors
        osdViewer.addHandler('open-failed', (event) => {
            console.warn('OpenSeadragon open failed:', event);
        });

        osdViewer.addHandler('tile-load-failed', (event) => {
            console.warn('OpenSeadragon tile load failed:', event);
        });

        // Add mouse tracking event handlers
        osdViewer.addHandler('mouse-move', (event) => {
            try {
                // console.log('Mouse move event:', event);
                const webPoint = event.position;
                // console.log('Web point:', webPoint);

                // webPoint from OpenSeadragon events should already be a Point object
                const viewportPoint = osdViewer.viewport.pointFromPixel(webPoint);
                // console.log('Viewport point:', viewportPoint);
                const imagePoint = osdViewer.viewport.viewportToImageCoordinates(viewportPoint);
                // console.log('Image point:', imagePoint);

                setMouseCoords({
                    webPoint: { x: webPoint.x, y: webPoint.y },
                    imagePoint: { x: imagePoint.x, y: imagePoint.y },
                    viewportPoint: { x: viewportPoint.x, y: viewportPoint.y }
                });
            } catch (error) {
                // Suppress OpenSeadragon internal errors like forcedRedraw
                if (!error.message?.includes('forcedRedraw')) {
                    console.warn('Mouse tracking error:', error);
                }
            }
        });

        // Also add canvas-level mouse tracking as backup
        osdViewer.addHandler('canvas-move', (event) => {
            try {
                const webPoint = event.position;
                // webPoint from OpenSeadragon events should already be a Point object
                const viewportPoint = osdViewer.viewport.pointFromPixel(webPoint);
                const imagePoint = osdViewer.viewport.viewportToImageCoordinates(viewportPoint);

                setMouseCoords({
                    webPoint: { x: webPoint.x, y: webPoint.y },
                    imagePoint: { x: imagePoint.x, y: imagePoint.y },
                    viewportPoint: { x: viewportPoint.x, y: viewportPoint.y }
                });
            } catch (error) {
                // Suppress OpenSeadragon internal errors like forcedRedraw
                if (!error.message?.includes('forcedRedraw')) {
                    console.warn('Canvas mouse tracking error:', error);
                }
            }
        });

        // Add direct canvas mouse tracking as final backup (with delay to ensure canvas is ready)
        setTimeout(() => {
            const canvas = osdViewer.canvas;
            // console.log('Canvas found:', canvas);
            if (canvas) {
                canvas.addEventListener('mousemove', (event) => {
                    try {
                        // console.log('Direct canvas mouse move:', event);
                        const rect = canvas.getBoundingClientRect();
                        const webPoint = {
                            x: event.clientX - rect.left,
                            y: event.clientY - rect.top
                        };
                        // console.log('Direct web point:', webPoint);

                        // Create OpenSeadragon Point object
                        const osdPoint = new OpenSeadragon.Point(webPoint.x, webPoint.y);
                        const viewportPoint = osdViewer.viewport.pointFromPixel(osdPoint);
                        // console.log('Direct viewport point:', viewportPoint);
                        const imagePoint = osdViewer.viewport.viewportToImageCoordinates(viewportPoint);
                        // console.log('Direct image point:', imagePoint);

                        const newCoords = {
                            webPoint: { x: webPoint.x, y: webPoint.y },
                            imagePoint: { x: imagePoint.x, y: imagePoint.y },
                            viewportPoint: { x: viewportPoint.x, y: viewportPoint.y }
                        };
                        // console.log('Setting new coords:', newCoords);
                        setMouseCoords(newCoords);
                        // Update counter removed - not needed
                    } catch (error) {
                        // Suppress OpenSeadragon internal errors like forcedRedraw
                        if (!error.message?.includes('forcedRedraw')) {
                            console.warn('Direct canvas mouse tracking error:', error);
                        }
                    }
                });
            }
        }, 100);

        return () => {
            // Restore original error handler
            window.onerror = originalError;

            osdInstanceRef.current = null;
            if (osdViewer) {
                osdViewer.destroy();
            }
        };
    }, []);

    // Grow/shrink with panel layout (replaces fixed 400px height)
    useEffect(() => {
        if (!viewerContainerRef.current) return;

        const container = viewerContainerRef.current;
        const resizeViewer = () => {
            const osdViewer = osdInstanceRef.current;
            if (!osdViewer || typeof osdViewer.resize !== 'function') return;
            if (container.clientWidth <= 0 || container.clientHeight <= 0) return;
            try {
                osdViewer.resize();
            } catch (error) {
                console.warn('OpenSeadragon resize skipped:', error);
            }
        };

        const observer = new ResizeObserver(resizeViewer);
        observer.observe(container);
        resizeViewer();

        return () => observer.disconnect();
    }, [viewer]);

    // Load image when selectedFolder or selectedImage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- OpenSeadragon load pipeline; narrow deps avoid reload storms
    useEffect(() => {
        if (!viewer) return;

        const effectStartTime = performance.now();
        const generation = ++loadGenerationRef.current;
        const folderSnapshot = selectedFolder;

        setIsLoading(true);

        // Only show spinner if load takes longer than 100ms
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }
        loadingTimeoutRef.current = setTimeout(() => {
            setShowLoadingSpinner(true);
        }, 100);

        // Clear existing images and overlays; abort in-flight tiles from the previous image
        const clearStart = performance.now();
        dermCoregOverlayGenRef.current += 1;
        clearDermCoregOverlayRefs();
        resetViewerBeforeOpen(viewer);
        clearROIs();
        console.log(`🔵 [LOAD EFFECT] Clear took ${(performance.now() - clearStart).toFixed(2)}ms`);

        let imageToLoad = null;

        if (isDermoscopyViewer) {
            if (selectedImage) {
                imageToLoad = selectedImage;
            }
        } else if (isNavigationViewer) {
            if (selectedImage) {
                // Navigation viewer: load the selected image
                imageToLoad = selectedImage;
                console.log('🔍 Navigation viewer loading selected image:', {
                    description: selectedImage.description,
                    type: selectedImage.type,
                    itemid: selectedImage.itemid,
                    yamlId: selectedImage.yamlId,
                    fullObject: selectedImage
                });
            } else if (selectedFolder && selectedFolder.meta && selectedFolder.meta.registrationData) {
                // Navigation viewer: load first item from registration data if no selection
                const registrationData = selectedFolder.meta.registrationData;
                if (Array.isArray(registrationData) && registrationData.length > 0) {
                    imageToLoad = registrationData[0];
                    console.log('Navigation viewer loading first registration item:', imageToLoad);
                }
            }
        } else if (!isNavigationViewer && selectedFolder) {
            // Main viewer: load macroscopic image from folder
            imageToLoad = getMacroscopicImage(selectedFolder);
            console.log('Main viewer loading macroscopic image:', imageToLoad);
        }

        const handleOpen = () => {
            if (generation !== loadGenerationRef.current) return;
            console.log('✅ Image loaded successfully');
            setIsLoading(false);
            // Redraw ROI boxes after the macroscopic image is ready.
            if (
                !isNavigationViewer &&
                !isDermoscopyViewer &&
                folderSnapshot?.meta?.registrationData
            ) {
                drawROIsFromRegistrationData(folderSnapshot.meta.registrationData);
            }
            if (!isNavigationViewer && !isDermoscopyViewer) {
                window.requestAnimationFrame(() => {
                    syncDermCoregOverlayRef.current();
                });
            }
        };

        const handleOpenFailed = (event) => {
            if (generation !== loadGenerationRef.current) return;
            console.error('❌ Failed to load image:', event);

            // Clear the loading timeout and hide spinner
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            setIsLoading(false);
            setShowLoadingSpinner(false);
        };

        viewer.addOnceHandler('open', handleOpen);
        viewer.addOnceHandler('open-failed', handleOpenFailed);

        if (imageToLoad && (imageToLoad.yamlId || imageToLoad._id)) {
            // Set max slices from the image data
            if (imageToLoad.slices) {
                const slices = parseInt(imageToLoad.slices) || 0;
                console.log('Setting max slices:', { slices, imageToLoad });
                setMaxSlices(slices);

                if (isNavigationViewer && slices > 1) {
                    // Load all slices for navigation viewer
                    console.log('🔵 [LOAD EFFECT] Calling loadZStack for z-stack');
                    const zstackCallTime = performance.now();
                    console.log(`   Time since effect start: ${(zstackCallTime - effectStartTime).toFixed(2)}ms`);

                    loadZStack(viewer, imageToLoad, slices, 0, () => {
                        if (generation !== loadGenerationRef.current) return;
                        // Turn off loading when first slice is ready
                        if (loadingTimeoutRef.current) {
                            clearTimeout(loadingTimeoutRef.current);
                        }
                        setIsLoading(false);
                        setShowLoadingSpinner(false);
                    }, generation);
                } else {
                    // Load single slice for main viewer or single-slice images
                    console.log('🔵 [LOAD EFFECT] Calling viewer.open() for single image');
                    const singleCallTime = performance.now();
                    console.log(`   Time since effect start: ${(singleCallTime - effectStartTime).toFixed(2)}ms`);

                    const tileSourceUrl = createDZITileSource(imageToLoad, 0);
                    try {
                        viewer.open(tileSourceUrl);
                    } catch (error) {
                        console.error('Error opening tile source:', error);
                        setIsLoading(false);
                    }
                }
            } else {
                console.log('No slices field found in image:', imageToLoad);
                setMaxSlices(0);
                // Load single image
                const tileSourceUrl = createDZITileSource(imageToLoad, 0);
                try {
                    viewer.open(tileSourceUrl);
                } catch (error) {
                    console.error('Error opening tile source:', error);
                    setIsLoading(false);
                }
            }
        } else {
            console.log('No image found');
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
            setIsLoading(false);
            setShowLoadingSpinner(false);
        }
    // Main viewer reloads only when folder or macro image identity changes — not on every meta patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- OpenSeadragon load pipeline
    }, [
        viewer,
        selectedFolder?._id,
        !isNavigationViewer && !isDermoscopyViewer
            ? getImageItemId(getMacroscopicImage(selectedFolder))
            : null,
        isNavigationViewer || isDermoscopyViewer
            ? getImageItemId(selectedImage)
            : null,
        isNavigationViewer,
        isDermoscopyViewer,
    ]);

    // New image → slice 0; slice changes alone use updateSliceVisibility (no full reload)
    useEffect(() => {
        if (isNavigationViewer) {
            setCurrentSlice(0);
        }
    }, [
        isNavigationViewer,
        isNavigationViewer || isDermoscopyViewer
            ? getImageItemId(selectedImage)
            : null,
    ]);

    // Update ROI visibility when it changes (only for main viewer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only when roiVisibilities changes
    useEffect(() => {
        console.log('Visibility useEffect triggered:', {
            isNavigationViewer,
            hasViewer: !!viewer,
            roiVisibilities,
            overlayCount: Object.keys(roiOverlaysRef.current).length
        });

        if (viewer && !isNavigationViewer && !isDermoscopyViewer) {
            console.log('Updating visibilities in MAIN viewer');
            // Update visibility for each changed ROI
            Object.entries(roiVisibilities).forEach(([index, isVisible]) => {
                updateROIVisibility(parseInt(index), isVisible);
            });
        } else if (isNavigationViewer) {
            console.log('Skipping visibility update - this is NAVIGATION viewer');
        }
    }, [roiVisibilities]);

    // Manage tiled image overlays when activeTiledOverlays changes (only for main viewer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlay helpers recreated each render
    useEffect(() => {
        console.log('🖼️ Overlay useEffect triggered:', {
            hasViewer: !!viewer,
            isNavigationViewer,
            activeTiledOverlays,
            worldItemCount: viewer?.world?.getItemCount()
        });

        if (!viewer || isNavigationViewer || isDermoscopyViewer) {
            console.log('🖼️ Skipping overlay update - no viewer or side panel');
            return;
        }

        // Wait for the base image to be loaded
        const baseImage = viewer.world.getItemAt(0);
        if (!baseImage) {
            console.warn('🖼️ No base image loaded yet, waiting...');
            return;
        }

        console.log('🖼️ activeTiledOverlays changed:', activeTiledOverlays);

        // Get current overlay indices
        const currentOverlayIndices = Object.keys(tiledOverlaysRef.current).map(Number);
        const requestedOverlayIndices = Object.keys(activeTiledOverlays).map(Number);

        console.log('🖼️ Current overlays:', currentOverlayIndices);
        console.log('🖼️ Requested overlays:', requestedOverlayIndices);

        // Remove overlays that are no longer active
        currentOverlayIndices.forEach(index => {
            if (!requestedOverlayIndices.includes(index)) {
                console.log(`🗑️ Removing overlay for ROI ${index}`);
                removeTiledImageOverlay(index);
            }
        });

        // Add new overlays that aren't currently displayed
        requestedOverlayIndices.forEach(index => {
            if (!currentOverlayIndices.includes(index)) {
                const registrationItem = activeTiledOverlays[index];
                console.log(`➕ Adding overlay for ROI ${index}:`, registrationItem);
                addTiledImageOverlay(registrationItem, index);
            }
        });
    }, [activeTiledOverlays, viewer, isNavigationViewer]);

    // Redraw ROI boxes when registrationData changes (e.g. after co-reg save)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- drawROIsFromRegistrationData recreated each render
    useEffect(() => {
        if (!viewer || isNavigationViewer || isDermoscopyViewer) return;
        const registrationData = selectedFolder?.meta?.registrationData;
        if (!registrationData) return;
        const baseImage = viewer.world.getItemAt(0);
        if (!baseImage) return;
        drawROIsFromRegistrationData(registrationData);
    }, [selectedFolder?.meta?.registrationData, viewer, isNavigationViewer, isDermoscopyViewer]);

    // Highlight selected registration row with dashed ROI border
    useEffect(() => {
        if (!viewer || isNavigationViewer || isDermoscopyViewer) return;
        updateROISelectionStyles();
    }, [selectedRegistrationIndex, viewer, isNavigationViewer, isDermoscopyViewer]);

    // Derm co-registration overlay on macroscopic viewer (from folder.meta.coreg)
    useEffect(() => {
        syncDermCoregOverlayRef.current();
    }, [
        dermCoregImage,
        coregState,
        dermCoregOpacity,
        viewer,
        isNavigationViewer,
        isDermoscopyViewer,
    ]);

    // Update slice visibility when currentSlice changes (navigation viewer only)
    useEffect(() => {
        if (viewer && isNavigationViewer && maxSlices > 1) {
            updateSliceVisibility(viewer, currentSlice, maxSlices);
        }
    }, [viewer, currentSlice, maxSlices, isNavigationViewer]);

    const handleZoomIn = () => {
        if (viewer) {
            const currentZoom = viewer.viewport.getZoom();
            viewer.viewport.zoomTo(currentZoom * 1.5);
        }
    };

    const handleZoomOut = () => {
        if (viewer) {
            const currentZoom = viewer.viewport.getZoom();
            viewer.viewport.zoomTo(currentZoom / 1.5);
        }
    };

    const handleHome = () => {
        if (viewer) {
            viewer.viewport.goHome();
        }
    };

    const handleFullscreen = () => {
        if (viewer) {
            viewer.setFullScreen(!viewer.isFullPage());
        }
    };

    return (
        <div className="openseadragon-viewer">
            <div className="viewer-header">
                <h3>
                    {isDermoscopyViewer
                        ? (selectedImage
                            ? (selectedImage.isic_id || selectedImage.description || 'Dermoscopy')
                            : 'Dermoscopy')
                        : isNavigationViewer
                        ? (selectedImage
                            ? (selectedImage.description || selectedImage.itemid || selectedImage.yamlId || 'RCM Stack')
                            : 'RCM Stack')
                        : 'Macroscopic'}
                </h3>
                <div className="viewer-controls">
                    {!isNavigationViewer && !isDermoscopyViewer && (
                        <>
                            <button onClick={handleZoomIn} title="Zoom In">
                                <i className="fas fa-search-plus"></i>
                            </button>
                            <button onClick={handleZoomOut} title="Zoom Out">
                                <i className="fas fa-search-minus"></i>
                            </button>
                            <button onClick={handleHome} title="Home">
                                <i className="fas fa-home"></i>
                            </button>
                            <button onClick={handleFullscreen} title="Fullscreen">
                                <i className="fas fa-expand"></i>
                            </button>
                            <button onClick={clearROIs} title="Clear ROIs">
                                <i className="fas fa-times"></i>
                            </button>
                            <button onClick={() => {
                                if (selectedFolder && selectedFolder.meta && selectedFolder.meta.registrationData) {
                                    drawROIsFromRegistrationData(selectedFolder.meta.registrationData);
                                }
                            }} title="Draw ROIs">
                                <i className="fas fa-square"></i>
                            </button>
                        </>
                    )}
                    {isNavigationViewer && maxSlices > 1 && (
                        <div className="slice-controls">
                            <label>Slice:</label>
                            <input
                                type="range"
                                min="0"
                                max={maxSlices - 1}
                                value={currentSlice}
                                onChange={(e) => {
                                    const newSlice = parseInt(e.target.value);
                                    console.log('Slice slider changed:', { newSlice, maxSlices });
                                    setCurrentSlice(newSlice);
                                }}
                                className="slice-slider"
                            />
                            <span className="slice-info">{currentSlice + 1} / {maxSlices}</span>
                        </div>
                    )}
                    {/* Debug info */}
                    {isNavigationViewer && (
                        <div style={{ fontSize: '10px', color: '#666', marginLeft: '10px' }}>
                            Debug: maxSlices={maxSlices}, currentSlice={currentSlice}
                        </div>
                    )}
                </div>
            </div>
            <div className="viewer-container" ref={viewerContainerRef}>
                <div ref={viewerRef} className="openseadragon-container" />
                {!isNavigationViewer && (
                    <div className="mouse-coords-display">
                        <div className="coord-item">
                            <span>Web Coords: {mouseCoords.webPoint.x.toFixed(2)}, {mouseCoords.webPoint.y.toFixed(2)}</span>
                        </div>
                        <div className="coord-item">
                            <span>Image Coords: {mouseCoords.imagePoint.x.toFixed(2)}, {mouseCoords.imagePoint.y.toFixed(2)}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OpenSeadragonViewer;

